<template>
  <div class="flex relative items-center space-x-4 bg-gray-800 rounded-lg p-4">
    <!-- Bouton Play/Pause -->
    <button
      @click="togglePlayPause"
      :disabled="practiceType === 'exam' && hasBeenPlayed"
      class="flex items-center justify-center w-10 h-10 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
      :class="getPlayButtonClass()"
    >
      <!-- Icône Play -->
      <svg v-if="!isPlaying" class="w-5 h-5 ml-0.5" :class="getIconClass()" fill="currentColor" viewBox="0 0 20 20">
        <path d="M8 5v10l8-5-8-5z"/>
      </svg>
      <!-- Icône Pause -->
      <svg v-else class="w-5 h-5" :class="getIconClass()" fill="currentColor" viewBox="0 0 20 20">
        <path d="M5.5 3.5A1.5 1.5 0 017 2h6a1.5 1.5 0 011.5 1.5v13A1.5 1.5 0 0113 18H7a1.5 1.5 0 01-1.5-1.5v-13zM8.5 5v8h3V5h-3z"/>
      </svg>
    </button>

    <!-- Informations et barre de progression -->
    <div class="flex-1 min-w-0">
      <!-- Titre et indication -->
      <div class="text-white text-sm font-medium truncate mb-1" v-auto-animate>
        {{ title || 'Exercice de compréhension orale' }}
        <span v-if="practiceType === 'exam' && hasBeenPlayed" class="text-red-400 ml-2">(Déjà écouté)</span>
        <span v-else-if="isPlaying" class="text-green-400 ml-2">(En cours...)</span>
        <span v-else-if="practiceType === 'training' && hasBeenPlayed" class="text-blue-400 ml-2">(En pause)</span>
      </div>

      <!-- Barre de progression -->
      <div class="flex items-center space-x-2">
        <span class="text-gray-400 text-xs tabular-nums">{{ formatTime(currentTime) }}</span>

        <div
          class="flex-1 h-2 bg-gray-600 rounded-full relative"
          :class="practiceType === 'training' ? 'cursor-pointer' : 'cursor-default'"
          @click="onProgressBarClick"
        >
          <div
            class="h-full bg-blue-600 rounded-full transition-all duration-75"
            :style="{ width: progressPercentage + '%' }"
          ></div>
          <!-- Indicateur de progression -->
          <div
            class="absolute top-1/2 w-3 h-3 bg-blue-600 rounded-full transform -translate-y-1/2 transition-opacity"
            :class="isPlaying ? 'opacity-100' : (practiceType === 'training' ? 'opacity-75' : 'opacity-0')"
            :style="{ left: 'calc(' + progressPercentage + '% - 6px)' }"
          ></div>
        </div>

        <span class="text-gray-400 text-xs tabular-nums">{{ formatTime(duration) }}</span>
      </div>

      <!-- Indicateur du mode -->
      <div class="mt-1 text-xs absolute bottom-0 right-1" :class="practiceType === 'training' ? 'text-green-400' : 'text-orange-400'">
        <i class="fa-solid" :class="practiceType === 'training' ? 'fa-dumbbell' : 'fa-stopwatch'"></i>
      </div>
    </div>

    <!-- Contrôle du volume (uniquement en mode training) -->
    <div v-if="practiceType === 'training'" class="flex items-center space-x-2">
      <button
        @click="toggleMute"
        class="text-gray-400 hover:text-white transition-colors"
      >
        <svg v-if="!isMuted && volume > 0.5" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.776L4.414 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.414l3.969-3.776zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.983 5.983 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.984 3.984 0 00-1.172-2.828 1 1 0 010-1.415z" clip-rule="evenodd"/>
        </svg>
        <svg v-else-if="!isMuted && volume > 0" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.776L4.414 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.414l3.969-3.776zM12.828 5.172a1 1 0 011.415 0A3.984 3.984 0 0116 10a3.984 3.984 0 01-1.757 2.828 1 1 0 01-1.415-1.415A1.984 1.984 0 0014 10c0-.552-.224-1.052-.586-1.414a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
        <svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.776L4.414 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.414l3.969-3.776zM12 8l3 3m0-3l-3 3" clip-rule="evenodd"/>
        </svg>
      </button>
      <input
        type="range"
        min="0"
        max="1"
        step="0.1"
        v-model="volume"
        @input="updateVolume"
        class="w-16 h-1 bg-gray-600 rounded-lg appearance-none slider cursor-pointer"
      >
    </div>

    <!-- Volume désactivé en mode examen -->
    <div v-else class="flex items-center space-x-2 opacity-50">
      <button disabled class="text-gray-500 cursor-not-allowed">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.776L4.414 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.414l3.969-3.776zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.983 5.983 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.984 3.984 0 00-1.172-2.828 1 1 0 010-1.415z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <!-- Élément audio caché -->
    <audio
      ref="audioElement"
      @loadedmetadata="onLoadedMetadata"
      @timeupdate="onTimeUpdate"
      @ended="onEnded"
      @error="onError"
      preload="metadata"
    >
      <source :src="src" :type="audioType">
      Votre navigateur ne supporte pas l'élément audio.
    </audio>
  </div>
</template>

<script>
export default {
  name: 'AudioPlayer',
  props: {
    src: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    practiceType: {
      type: String,
      default: 'training', // 'training' ou 'exam'
      validator: (value) => ['training', 'exam'].includes(value)
    }
  },
  data() {
    return {
      isPlaying: false,
      hasBeenPlayed: false,
      currentTime: 0,
      duration: 0,
      volume: 1,
      isMuted: false,
      previousVolume: 1
    }
  },
  computed: {
    progressPercentage() {
      if (this.duration === 0) return 0;
      return (this.currentTime / this.duration) * 100;
    },
    audioType() {
      if (!this.src) return '';
      const extension = this.src.split('.').pop().toLowerCase();
      const typeMap = {
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav',
        'ogg': 'audio/ogg',
        'm4a': 'audio/mp4',
        'aac': 'audio/aac'
      };
      return typeMap[extension] || 'audio/mpeg';
    }
  },
  watch: {
    src() {
      this.loadAudio();
    },
    volume(newVolume) {
      if (this.$refs.audioElement) {
        this.$refs.audioElement.volume = this.isMuted ? 0 : newVolume;
      }
    }
  },
  mounted() {
    this.loadAudio();
  },
  methods: {
    loadAudio() {
      if (this.src) {
        this.$refs.audioElement.load();
        // Réinitialiser les états si on change de fichier
        this.hasBeenPlayed = false;
        this.isPlaying = false;
        this.currentTime = 0;
      }
    },

    togglePlayPause() {
      // En mode exam, ne peut pas rejouer si déjà écouté
      if (this.practiceType === 'exam' && this.hasBeenPlayed) {
        return;
      }

      if (this.isPlaying) {
        // Pause seulement en mode training
        if (this.practiceType === 'training') {
          this.pause();
        }
      } else {
        this.play();
      }
    },

    play() {
      this.$refs.audioElement.play()
        .then(() => {
          this.isPlaying = true;
          if (this.practiceType === 'exam') {
            this.hasBeenPlayed = true; // Marquer comme écouté dès le début en mode examen
          }
          this.$emit('play');
        })
        .catch(error => {
          console.error('Erreur de lecture:', error);
          this.$emit('error', error);
        });
    },

    pause() {
      if (this.practiceType === 'training') {
        this.$refs.audioElement.pause();
        this.isPlaying = false;
        this.$emit('pause');
      }
    },

    onProgressBarClick(event) {
      // Contrôle de position seulement en mode training
      if (this.practiceType !== 'training') return;

      const progressBar = event.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const progressBarWidth = rect.width;
      const clickPercentage = clickX / progressBarWidth;
      const newTime = clickPercentage * this.duration;

      this.$refs.audioElement.currentTime = newTime;
      this.currentTime = newTime;
    },

    toggleMute() {
      if (this.practiceType !== 'training') return;

      this.isMuted = !this.isMuted;
      if (this.isMuted) {
        this.previousVolume = this.volume;
        this.$refs.audioElement.volume = 0;
      } else {
        this.volume = this.previousVolume;
        this.$refs.audioElement.volume = this.volume;
      }
    },

    updateVolume() {
      if (this.practiceType !== 'training') return;

      this.isMuted = false;
      this.$refs.audioElement.volume = this.volume;
    },

    getPlayButtonClass() {
      if (this.practiceType === 'exam' && this.hasBeenPlayed) {
        return 'bg-gray-600 cursor-not-allowed';
      }
      return 'bg-blue-600 hover:bg-blue-700';
    },

    getIconClass() {
      if (this.practiceType === 'exam' && this.hasBeenPlayed) {
        return 'text-gray-400';
      }
      return 'text-white';
    },

    formatTime(seconds) {
      if (isNaN(seconds)) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },

    onLoadedMetadata() {
      this.duration = this.$refs.audioElement.duration;
      this.$refs.audioElement.volume = this.volume;
      this.$emit('loadedmetadata', {
        duration: this.duration
      });
    },

    onTimeUpdate() {
      this.currentTime = this.$refs.audioElement.currentTime;
      this.$emit('timeupdate', {
        currentTime: this.currentTime,
        duration: this.duration
      });
    },

    onEnded() {
      this.isPlaying = false;
      if (this.practiceType === 'training') {
        this.hasBeenPlayed = true; // Marquer comme écouté à la fin en mode training
      }
      this.$emit('ended');
    },

    onError(error) {
      console.error('Erreur audio:', error);
      this.$emit('error', error);
    }
  }
}
</script>

<style scoped>
/* Style personnalisé pour le slider de volume */
.slider::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3B82F6;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3B82F6;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.slider::-webkit-slider-track {
  background: #4B5563;
  border-radius: 4px;
}

.slider::-moz-range-track {
  background: #4B5563;
  border-radius: 4px;
  border: none;
}
</style>
